<template>
  <div class="front-page margin-xl-vertical">
    <div class="content-section container container-xl" style="min-height: 200px">
      <div
        v-if="!contentSectionItems"
        class="flex-middle flex-center width-expand"
        style="min-height: 200px">
        <spinner :size="5"></spinner>
      </div>
      <div
        v-if="contentSectionItems"
        class="content-section-inner flex width-expand flex-column l:flex-row gap-xl">
        <div class="main-content">
          <ag-title
            text="ag.home.selectedArticles"
            class="margin-m-bottom main-content-title"></ag-title>
          <minimal-intro
            class="main-article content-intro"
            :title="contentSectionItems.blogArticle.title"
            :image-url="
              isNonEmptyString(contentSectionItems.blogArticle.thumbnailImage)
                ? contentSectionItems.blogArticle.thumbnailImage
                : contentSectionItems.blogArticle.mainImageUrl
            "
            :show-badge="false"
            :text="contentSectionItems.blogArticle.introText"
            :item="contentSectionItems.blogArticle"></minimal-intro>
        </div>
        <div class="secondary-content flex-column gap-xl flesh-stretch">
          <minimal-intro
            :show-badge="true"
            :title="contentSectionItems.newsFlashArticle.title"
            :image-url="
              isNonEmptyString(contentSectionItems.newsFlashArticle.thumbnailImage)
                ? contentSectionItems.newsFlashArticle.thumbnailImage
                : contentSectionItems.newsFlashArticle.mainImageUrl
            "
            :badge-text="'ag.home.newsflashBadgeText'"
            :badge-theme="'danger'"
            :text="contentSectionItems.newsFlashArticle.introText"
            :item="contentSectionItems.newsFlashArticle"
            class="secondary-article secondary-article--1 content-intro"></minimal-intro>
          <minimal-intro
            :show-badge="true"
            :badge-text="'ag.home.termBadgeText'"
            :badge-theme="'alt'"
            :image-url="getDynamicAssetUrl('images/homepage/term-item-bg.png')"
            :title="contentSectionItems.term.title"
            :text="contentSectionItems.term.content"
            :item="contentSectionItems.term"
            class="secondary-article secondary-article--2 content-intro"></minimal-intro>
        </div>
      </div>
    </div>
    <div class="separator-section width-expand flex flex-center margin-xl-vertical">
      <icon icon="chevron-down" size="4" theme="gray-4"></icon>
    </div>
    <div class="store-section padding-xl-top padding-max-bottom relative">
      <div
        class="bg absolute-cover z-level-1 m:visible"
        :style="`background-image: url('${getDynamicAssetUrl(
          'images/homepage/store-bg-wide.svg'
        )}');`"></div>
      <div
        class="bg absolute-cover z-level-1 m:hidden"
        :style="`background-image: url('${getDynamicAssetUrl(
          'images/homepage/store-bg-desktop.svg'
        )}');`"></div>
      <div
        class="container container-xl margin-inline-auto relative z-level-2 flex flex-center s:flex-end">
        <div class="content text-center flex-column flex-middle flex-center">
          <div class="content-inner">
            <ag-title
              theme="alt-2"
              class="inline-block margin-s-bottom l:margin-l-bottom width-expand margin-inline-auto"
              level="2"
              text="ag.home.storeTitle"></ag-title>
            <p
              class="width-expand margin-inline-auto margin-remove-top text-gray-4"
              style="line-height: 1.66">
              {{ tu("ag.home.storeText") }}
            </p>
            <div
              class="store-cta flex width-expand margin-inline-auto flex-center margin-xl-top l:margin-2xl-top">
              <form-button
                icon-end="chevron-inline-end"
                theme="alt-2"
                text="ag.home.storeCta"
                @click="$router.push({ name: 'store' })"></form-button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="newsletter-section overflow-x-hidden margin-max-top margin-xl-bottom l:margin-max-bottom relative">
      <div class="container container-xl relative z-level-2">
        <div class="newsletter-content-area max-width-500">
          <ag-title
            theme="lead-2"
            class="inline-block margin-l-bottom width-expand margin-inline-auto"
            level="2"
            text="ag.home.newsletterTitle"></ag-title>
          <p
            class="width-expand margin-inline-auto margin-remove-top text-gray-4"
            style="line-height: 1.66">
            {{ tu("ag.home.newsletterText") }}
          </p>
          <div
            v-if="newsLetterRegistered"
            class="flex-between margin-l-top l:margin-xl-top">
            <h3 class="margin-remove width-expand text-center padding-m-inline">
              {{ tu("ag.newsletterLead.registeredSuccessInlineText") }}
            </h3>
          </div>

          <div
            v-if="!newsLetterRegistered"
            class="newsletter-form-area border-radius bg-bg-5 border-thin-bg-5 flex-between margin-l-top l:margin-xl-top">
            <base-form
              ref="newsLetterForm"
              v-model="newsLetterData"
              method="post"
              error-display-position="absolute"
              action="agaloola/lead/newsletter"
              :field-wrapper-margin="false"
              :config="newsletterFormConfig"
              @form:submit-success="handleNewsletterSuccess"></base-form>
            <form-button
              theme="lead-2"
              class="border-radius newsletter-cta-button"
              text="ag.home.newsletterCta"
              @click="$refs?.newsLetterForm?.submit()"></form-button>
          </div>
        </div>
      </div>

      <div class="newsletter-section-graphics relative z-level-1">
        <!-- 'images/homepage/newsletter-section-strip.svg'-->
        <div
          class="absolute bg-strip bg-strip--desktop l:visible z-level-1"
          :style="`background-image: url('${getDynamicAssetUrl(
            'images/homepage/bg-wide.svg'
          )}');`"></div>
        <div
          class="absolute bg-strip bg-strip--mobile l:hidden z-level-1"
          :style="`background-image: url('${getDynamicAssetUrl(
            'images/homepage/newsletter-section-strip-mobile.svg'
          )}');`"></div>

        <div class="container container-xl car-image-container absolute z-level-2">
          <div
            class="absolute car-image z-level-2"
            :style="`background-image: url('${getDynamicAssetUrl(
              'images/homepage/newsletter-section-car.svg'
            )}');`"></div>
        </div>
      </div>
      <div class="newsletter-section-graphics-placeholder"></div>
    </div>
  </div>
</template>

<script>
import asyncOperations from "@/client/extensions/composition/asyncOperations.js";
import { useStore } from "vuex";
import MinimalIntro from "@/client/applications/agaloola/components/items/generic/MinimalIntro";
export default {
  components: { MinimalIntro },
  layout: {
    component: "default",
    props: {},
  },
  middleware: {},
  setup(props) {
    const { asyncOps, asyncOpsReady, asyncStatus } = asyncOperations(props, useStore());
    return { asyncOps, asyncOpsReady, asyncStatus };
  },
  meta: {
    title: "ag.meta.home.title",
    description: "ag.meta.home.description",
  },
  data() {
    return {
      newsLetterData: {},
      contentSectionItems: false,
      newsLetterRegistered: false,
      newsletterFormConfig: {
        fields: {
          email: {
            class: "newsletter-email-field",
            name: "email",
            type: "email",
            label: false,
            placeholder: "ag.home.newsletterEmailPlaceholder",
            validation: {
              required: {},
              email: {},
            },
            //autoInputStyling: false,
            themeStyle: "minimal",
          },
        },
        buttons: {},
      },
      asyncData: {
        contentSectionItems: {
          target: "agaloola/home/main-content",
          data: {},
          options: {
            dataMutator(res) {
              console.log("main page data mutator");
              //   console.log(res);
              return res;
            },
          },
        },
      },
    };
  },
  computed: {},
  watch: {},
  mounted() {
    //  console.log('running server prefertch from mounted', this.serverPrefetch)
    try {
      //   this.serverPrefetch();
    } catch (e) {
      console.log(e);
    }
  },
  methods: {
    handleNewsletterSuccess() {
      this.newsLetterRegistered = true;
      this.$s.ui.notification("ag.newsletterLead.thankYou", "success");
    },
    isNonEmptyString(str) {
      return utilities.isNonEmptyString(str);
    },
  },

  serverPrefetch() {
    return this.getSSRPrefetchPromise();
  },
};
</script>

<style lang="scss" scoped>
.content-section {
  .content-section-inner {
    @media (max-width: 1024px) {
      max-width: 400px;
      margin-inline: auto;
    }

    @media (max-width: 767px) {
      max-width: 320px;
      margin-inline: auto;
    }
  }

  .main-content-title {
    @media (max-width: 1024px) {
      margin-inline: auto;
    }
  }

  .main-content {
    flex: 60%;

    @media (max-width: 1024px) {
      flex: 100%;
    }
  }

  .secondary-content {
    flex: 30%;

    @media (max-width: 1024px) {
      flex: 100%;
    }
  }

  .content-intro {
    border-radius: var(--border-radius);
    // background: rgba(0, 0, 0, 0.3);
  }

  .main-article {
    aspect-ratio: 1.5;

    @media (max-width: 1024px) {
      flex: auto;
      width: 100%;
      aspect-ratio: 1.5;
    }

    @media (max-width: 767px) {
      flex: auto;
      width: 100%;
      aspect-ratio: 1.2;
    }
  }

  .secondary-article--1 {
    flex: 40%;
    width: 100%;

    @media (max-width: 1024px) {
      flex: auto;
      width: 100%;
      aspect-ratio: 1.5;
    }
  }

  .secondary-article--2 {
    flex: 60%;
    width: 100%;

    @media (max-width: 1024px) {
      flex: auto;
      width: 100%;
      aspect-ratio: 1.5;
    }
  }
}

.store-section {
  .bg {
    position: absolute;
    background-repeat: no-repeat;
    background-position: 100% 0;
    background-size: contain;


    @media (min-width: 500px) {
      width: 50%;
      margin-inline-end: var(--margin-max);
      height: 350px;
      background-position: left top;
      background-size: auto 350px;
    }

    @media (max-width: 500px) {
      position: static;
      width: 100%;
      margin-inline-end: var(--margin-max);
      aspect-ratio: 1.51;
    }
  }

  .content {
    min-height: calc(350px -  var(--margin-xl) - var(--margin-max));
    max-width: 500px;
    padding-inline: var(--margin-2xl);

    @media (max-width: 900px) {

      max-width: 250px;
      padding: 0;
      padding-inline-start: var(--margin-m);
    }

    @media (max-width: 500px) {
      min-height: 0;

    }
  }
}

.newsletter-section {
  .newsletter-form-area {
    box-shadow: 0px 3px 3px 0px #000000;
    max-width: 400px;
    @media (max-width: 500px) {
      max-width: 300px;
    }
  }

  .newsletter-section-graphics-placeholder {
    min-height: 300px;

    @media (max-width: 1024px) {
      bottom: 0;
      min-height: 250px;
    }
  }
  :deep(.newsletter-email-field) {
    margin-top: 1px; // minor alignment fix
    .field__controls {
      border: none !important;
    }

    .field__element {
      @media (max-width: 500px) {
        padding-inline: var(--margin-s) !important;
        min-width: 0;
      }
    }
  }
  .newsletter-cta-button {
    @media (max-width: 500px) {
      padding-inline: var(--margin-m) !important;
    }
  }

  .newsletter-section-graphics {
    position: absolute;
    bottom: calc(0px + 35px);
    inset-inline-start: 0;
    width: 100%;
    height: 300px;

    @media (max-width: 1024px) {
      bottom: 0;
      height: 150px;
    }

    .bg-strip {
      height: 100%;
      width: 120vw;
      inset-inline-start: -10vw;
      bottom: 0;
      background-repeat: no-repeat;
      background-size: 100% 300px;
      background-position: center;

      &--mobile {
        width: 100%;
        height: 250px;
        inset-inline-start: 0;
        background-size: cover;
      }

      @media (max-width: 1024px) {
        height: 200px;
        aspect-ratio: none;
        background-size: cover;
        background-position: center;
      }
    }

    .car-image-container {
      left: 50%;
      transform: translateX(-50%);
      top: 0;
      width: var(--container-l);
      max-width: 100%;
    }

    .car-image {
      position: absolute;
      top: -50px;
      inset-inline-end: 100px;
      inset-inline-start: auto;
      aspect-ratio: 2.2;
      width: 535px;
      background-size: 100% auto;
      background-repeat: no-repeat;

      @media (max-width: 1024px) {
        width: 300px;
        inset-inline-end: calc(50% - 250px);
        inset-inline-start: auto;
      }

      @media (max-width: 767px) {
        width: 260px;
        inset-inline-end: 30px;
        inset-inline-start: auto;
      }
    }
  }
}
</style>
